import React, { useMemo } from "react";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { CRMChannel } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { InstagramAccountCard } from "../../components/InstagramAccountCard";
import { WhatsappAccountCard } from "../../components/WhatsappAccountCard";
import { WebChatBotAccountCard } from "../../components/WebChatBotAccountCard/WebChatBotAccountCard";
import { TwilioAccountCard } from "../../components/TwilioAccountCard/TwilioAccountCard";
import { VoiceAccountCard } from "../../components/VoiceAccountCard";
import { CRMDrawerTabsLayout } from "../../components/CRMDrawerTabsLayout";
import { LiveChatAppAccountCard } from "../../components/LiveChatAppAccountCard";
import { EmailAccountCard } from "../../components/EmailAccountCard";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

const channelComponents = {
  [CRMChannel.instagram]: InstagramAccountCard,
  [CRMChannel.whatsapp]: WhatsappAccountCard,
  [CRMChannel.chatbot]: WebChatBotAccountCard,
  [CRMChannel.twilio]: TwilioAccountCard,
  [CRMChannel.voice]: VoiceAccountCard,
  [CRMChannel.email]: EmailAccountCard,
};

export const ConnectedAccountListScreen: React.FC = () => {
  const { t } = useTranslations();
  const allChannels = useMemo(() => Object.values(CRMChannel), []);

  const { isCx } = useAppVariant();
  const { entity } = useEntitySelectedProvider();
  const { isConnected: isEmailConnected } = useEmailAccount();
  const { isCRMConfigLoading } = useManageAccountsDrawer();

  // Email is hidden on org/business workspaces unless it's already connected.
  const shouldHideEmailCard = entity.type === "org" && !isEmailConnected;
  const shouldHideVoiceCard = entity.type !== "cx";

  const content = (
    <div className="jugl__custom-scrollbar flex h-full min-h-0 w-full flex-col items-center overflow-y-auto p-10 pb-12 max-lg:p-5 max-lg:pb-8">
      <div className="flex w-full flex-col gap-10 max-lg:gap-6">
        <div className="flex flex-col gap-2">
          <span className="font-secondary text-2xl font-medium text-black">
            {t({
              id: "client-support-page.inbox-channels",
              defaultMessage: "Inbox Channels",
            })}
          </span>
          <span className="font-secondary text-[#828282]">
            {t({
              id: "client-support-page.inbox-channels-description",
              defaultMessage: "Customise and adjust your Inbox ✨",
            })}
          </span>
        </div>
        <div className="flex flex-wrap gap-8 max-lg:flex-col max-lg:gap-4">
          {allChannels.map((channel) => {
            const Component = channelComponents[channel];
            if (!Component) return null;
            if (channel === CRMChannel.email && shouldHideEmailCard) {
              return null;
            }
            if (channel === CRMChannel.voice && shouldHideVoiceCard) {
              return null;
            }

            return (
              <Component
                key={channel}
                isClickable={!isCRMConfigLoading}
                variant="tile"
              />
            );
          })}
          <LiveChatAppAccountCard
            isClickable={!isCRMConfigLoading}
            variant="tile"
          />
        </div>
      </div>
    </div>
  );

  if (isCx) {
    return content;
  }

  return <CRMDrawerTabsLayout>{content}</CRMDrawerTabsLayout>;
};
